import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _classCallCheck from "@babel/runtime/helpers/classCallCheck";
import _createClass from "@babel/runtime/helpers/createClass";
import _possibleConstructorReturn from "@babel/runtime/helpers/possibleConstructorReturn";
import _getPrototypeOf from "@babel/runtime/helpers/getPrototypeOf";
import _inherits from "@babel/runtime/helpers/inherits";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _callSuper(t, o, e) { return o = _getPrototypeOf(o), _possibleConstructorReturn(t, _isNativeReflectConstruct() ? Reflect.construct(o, e || [], _getPrototypeOf(t).constructor) : o.apply(t, e)); }
function _isNativeReflectConstruct() { try { var t = !Boolean.prototype.valueOf.call(Reflect.construct(Boolean, [], function () {})); } catch (t) {} return (_isNativeReflectConstruct = function _isNativeReflectConstruct() { return !!t; })(); }
import PropTypes from 'prop-types';
import React from 'react';
import { getLocaleNumericSeparators, isDigit, parseNumberFromPartialString, toPrecision } from '../../utils/numberUtil';

/**
 * Primitive for managing a formatted numeric input.
 *
 * You provide it a render function in `children` which
 * takes 2 args: props and actions (optional, only needed
 * if you want to manipulate this component's state.)
 *
 * Actions contains:
 * - changeValue: function to change the internal value without emitting.
 * - changeValueAndEmit: changes the internal value, parses and emits it as a change
 * - keyPressPreventNonNumeric: assign this to `onKeyPress` for an <input> field to
 *   prevent non-numeric characters from being entered (separators are allowed)
 * - isValidText: takes 1 argument and returns a boolean whether or not the value contains only numerics
 *   and separators (not whether it's actually a valid number)
 */
var NumericInput = /*#__PURE__*/function (_React$Component) {
  function NumericInput() {
    var _this;
    _classCallCheck(this, NumericInput);
    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }
    _this = _callSuper(this, NumericInput, [].concat(args));
    /**
     * Initial state.
     */
    _this.state = {
      value: _this.formatValueForInput(_this.props.value),
      wasChangedByInput: false
    };
    // /**
    //  * Updates the internal state according to prop changes.
    //  *
    //  * @param {*} nextProps
    //  */
    // componentWillReceiveProps(nextProps) {
    //   if (
    //     // If the props changed, reformat.
    //     this.props.value !== nextProps.value ||
    //     this.props.formatInputValue !== nextProps.formatInputValue ||
    //     this.props.mode !== nextProps.mode ||
    //     this.props.precision !== nextProps.precision ||
    //     this.props.locale !== nextProps.locale
    //   ) {
    //     // Only change the value if we are using blur mode, or if the
    //     // change was made due to a change event from the input.
    //     // Essentially, this prevents the input text from updating if the
    //     // change was caused by a change in the actual input and not an external prop change.
    //     if (nextProps.mode === 'blur' || !this.wasChangedByInput) {
    //       const value = this.formatValueForInput(nextProps.value, nextProps)
    //       this.setInputValue(value)
    //     }
    //     this.markWasChangedByInput(false)
    //   }
    // }
    /**
     * When the inner input changes, change the internal state.
     */
    _this.inputChange = function (e) {
      // It might not be an event object, it might just be the value itself.
      var value = typeof e === 'string' ? e : e.target.value;
      _this.markWasChangedByInput(true);
      if (_this.props.mode === 'blur') {
        _this.setInputValue(value);
      } else {
        _this.changeValueAndEmit(value, true);
      }
    };
    /**
     * On blur, propagate the changes.
     */
    _this.inputBlur = function (e) {
      _this.markWasChangedByInput(false);
      _this.changeValueAndEmit(e.target.value);
      if (_this.props.onBlur) {
        _this.props.onBlur(e);
      }
    };
    /**
     * Changes the inner value but does not emit.
     *
     * @param {string} str the value
     * @param {boolean} noFormat if true, does not format the value
     * @returns the formatted, fixed value
     */
    _this.changeValue = function (str, noFormat) {
      var value = _this.formatValueForInput(str);
      _this.setInputValue(noFormat ? str : value);
      return value;
    };
    /**
     * Triggers a change to the value which will be emitted.
     *
     * @param {string} str the value
     * @param {boolean} noFormat if true, does not format the value
     */
    _this.changeValueAndEmit = function (str, noFormat) {
      var value = _this.changeValue(str, noFormat);
      if (isEmptyValue(value)) {
        _this.emitValue(null);
      } else {
        var converted = parseNumber(value, _this.props);
        if (!Number.isNaN(converted)) {
          _this.emitValue(converted);
        } else {
          _this.emitValue(0);
        }
      }
    };
    /**
     * onKeyPress handler to prevent non-numeric characters from being entered into an input.
     */
    _this.keyPressPreventNonNumeric = function (e) {
      if (!_this.isValidText(String.fromCharCode(e.charCode))) {
        e.preventDefault();
        e.stopPropagation();
        return false;
      }
      return true;
    };
    /**
     * Determines if the value consists solely of valid characters used for composing a numeric string.
     *
     * @param {string} value
     */
    _this.isValidText = function (value) {
      if (_this.props.precision === 0) {
        return Array.from(value).every(function (ch, i) {
          return isDigit(ch);
        });
      }
      var separators = getSeparators(getLocale(_this.props));
      return Array.from(value).every(function (ch, i) {
        return isDigit(ch) || ch === '-' || ch === separators.thousand || ch === separators.decimal;
      });
    };
    return _this;
  }
  _inherits(NumericInput, _React$Component);
  return _createClass(NumericInput, [{
    key: "setInputValue",
    value:
    /**
     * Sets the underlying input's value.
     *
     * @param {*} value
     */
    function setInputValue(value) {
      this.setState(function (s) {
        return _objectSpread(_objectSpread({}, s), {}, {
          value: value
        });
      });
    }

    /**
     * Sets the wasChangedByInput flag.
     *
     * @param {*} wasChangedByInput
     */
  }, {
    key: "markWasChangedByInput",
    value: function markWasChangedByInput(wasChangedByInput) {
      this.wasChangedByInput = wasChangedByInput;
    }

    /**
     * Emits the value to the consumer.
     *
     * @param {*} value
     */
  }, {
    key: "emitValue",
    value: function emitValue(value) {
      if (this.props.onChange && value !== this.props.value) {
        this.props.onChange(toPrecision(value, getSanePrecision(this.props)));
      }
    }

    /**
     * Formats the value accordingly.
     *
     * @param {*} value the `Number` to format
     * @param {*} props caller can specify the prop bag to look at
     */
  }, {
    key: "formatValueForInput",
    value: function formatValueForInput(value) {
      var props = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : this.props;
      if (isEmptyValue(value)) {
        return '';
      }
      var parsed = toPrecision(parseNumber(value, props), getSanePrecision(props));
      if (Number.isNaN(parsed)) {
        return '';
      }
      if (!props.formatInputValue) {
        return parsed.toFixed(getSanePrecision(props));
      }
      return parsed.toLocaleString(getLocale(props), {
        minimumFractionDigits: getSanePrecision(props)
      });
    }

    /**
     * Gets a snapshot which gets passed to componentDidUpdate.
     * Used to make sure the input does not update while editing,
     * but that it also reflects prop changes.
     *
     * @param {*} prevProps
     * @param {*} prevState
     */
  }, {
    key: "getSnapshotBeforeUpdate",
    value: function getSnapshotBeforeUpdate(prevProps, prevState) {
      // If the props changed, reformat.
      if (!arePropsEqual(this.props, prevProps)) {
        // Only change the value if we are using blur mode, or if the
        // change was made due to a prop change and not the input changing.
        // Essentially, this prevents the input text from updating if the
        // change was caused by a change in the actual input and not an external prop change.
        if (prevProps.mode === 'blur' || !this.wasChangedByInput) {
          this.markWasChangedByInput(false);
          return {
            value: this.formatValueForInput(this.props.value, this.props)
          };
        }
      }
      return null;
    }

    /**
     * If the snapshot determined an update was necessary, set the new formatted value on state.
     *
     * @param {*} prevProp
     * @param {*} prevState
     * @param {*} snapshot
     */
  }, {
    key: "componentDidUpdate",
    value: function componentDidUpdate(prevProps, prevState, snapshot) {
      if (snapshot !== null) {
        this.setInputValue(snapshot.value);
      }
    }
  }, {
    key: "render",
    value:
    /**
     * Does the render thing.
     */
    function render() {
      var children = this.props.children;
      return children({
        value: isEmptyValue(this.state.value) ? '' : this.state.value,
        onChange: this.inputChange,
        onBlur: this.inputBlur
      }, {
        changeValue: this.changeValue,
        changeValueAndEmit: this.changeValueAndEmit,
        keyPressPreventNonNumeric: this.keyPressPreventNonNumeric,
        isValidText: this.isValidText
      });
    }
  }]);
}(React.Component);
/**
 * Determines if the props are equal.
 *
 * @param {*} leftProps
 * @param {*} rightProps
 */
/**
 * Default properties.
 */
NumericInput.defaultProps = {
  precision: 0,
  formatInputValue: false,
  locale: navigator.language,
  value: Number.NaN,
  onChange: null,
  mode: 'blur'
};
/**
 * Prop types.
 */
NumericInput.propTypes = {
  /**
   * Used to fix the number that gets emitted.
   */
  precision: PropTypes.number,
  /**
   * Whether to format the string that gets passed as `value` in the render props.
   */
  formatInputValue: PropTypes.bool,
  /**
   * The locale to format with.
   */
  locale: PropTypes.string,
  /**
   * The numeric value (Number type)
   */
  value: PropTypes.any,
  /**
   * Change handler. Can be triggered either with a DOM event or a string value.
   */
  onChange: PropTypes.func,
  /**
   * Whether to emit the value on blur (default) or live as the user enters the value.
   */
  mode: PropTypes.oneOf(['live', 'blur'])
};
export { NumericInput as default };
function arePropsEqual(leftProps, rightProps) {
  var didChange = !(leftProps.value === rightProps.value ||
  // NaN is apparently !== NaN, so..
  Number.isNaN(leftProps.value) && Number.isNaN(rightProps.value)) || leftProps.formatInputValue !== rightProps.formatInputValue || leftProps.mode !== rightProps.mode || leftProps.precision !== rightProps.precision || leftProps.locale !== rightProps.locale;
  return !didChange;
}

/**
 * Gets sanitized precision.
 */
function getSanePrecision(props) {
  var prec = parseInt(props.precision, 10);
  return Number.isNaN(prec) ? 0 : Math.max(0, prec);
}

/**
 * Gets the locale from props.
 *
 * @param {*} props
 */
function getLocale(props) {
  return props.locale;
}

/**
 * Determines if the value is empty (0 numeric does not count as empty)
 *
 * @param {*} value
 */
function isEmptyValue(value) {
  return value === null || value === undefined || value === '';
}

/**
 * Cache used for separator lookup.
 */
var separatorsCache = new Map();

/**
 * `getLocaleNumericSeparators` with caching.
 *
 * @param {*} locale
 */
function getSeparators(locale) {
  if (!separatorsCache.has(locale)) {
    separatorsCache.set(locale, getLocaleNumericSeparators(locale));
  }
  return separatorsCache.get(locale);
}

/**
 * Parses the number according to the specified props.
 *
 * @param {*} value
 * @param {*} props
 */
function parseNumber(value, props) {
  var locale = getLocale(props);
  return parseNumberFromPartialString(value, getSeparators(locale));
}
//# sourceMappingURL=index.js.map
